import React, { useState, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  TextField,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

const BASE_URL = 'https://jasperd.ca';

const NUMERIC_FIELDS = new Set([
  'commitmentLocal',
  'bow',
  'contributionRateYear1',
  'contributionRateYear2',
  'contributionRateYear3',
  'fundLifeYears',
  'growthYears6',
  'growthYears12',
  'growthYears35',
  'yield',
]);

const PacingInvestmentItem = ({
  fundName,
  portfolioInformation,
  initialFundInfo,
  historicData,
  futureCashflows,
  pacingData,
  scenarios,
  onPacingDataUpdate,
  onFundInfoUpdate,
}) => {
  const [fundInfo, setFundInfo] = useState(initialFundInfo || {});
  const [expanded, setExpanded] = useState(false);
  const [showInputs, setShowInputs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setFundInfo(initialFundInfo || {});
  }, [initialFundInfo]);

  const quarterlyData = useMemo(() => {
    if (!pacingData) return [];
    
    const rows = Object.entries(pacingData).map(([dt, values]) => ({
      date: new Date(dt).getTime(),
      marketValueLocal: values.marketValueLocal || 0,
      contributionsLocal: values.contributionsLocal || 0,
      distributionsLocal: values.distributionsLocal || 0,
      modelContributionsLocal: values.modelContributionsLocal || 0,
      modelDistributionsLocal: values.modelDistributionsLocal || 0,
    }));
    
    return rows.sort((a, b) => a.date - b.date);
  }, [pacingData]);

  const handleInputChange = (field, value) => {
    const newValue = NUMERIC_FIELDS.has(field) ? parseFloat(value) || 0 : value;
    setFundInfo(prev => ({ ...prev, [field]: newValue }));
  };

  const recalculateModel = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BASE_URL}/api/submit-pacing-data-dict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          portfolioInformation,
          fundInformation: { [fundName]: fundInfo },
          historicData: { [fundName]: historicData || {} },
          futureTransactions: { [fundName]: futureCashflows || {} },
          scenarios
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit pacing data');
      }

      const { task_id } = await response.json();
      
      const pollInterval = setInterval(async () => {
        const statusResponse = await fetch(`${BASE_URL}/api/task-status/${task_id}`);
        const statusData = await statusResponse.json();

        if (statusData.status === 'completed') {
          clearInterval(pollInterval);
          const newPacing = statusData.result.pacingData || {};
          console.log('newPacing', newPacing)
          if (newPacing[fundName]) {
            onPacingDataUpdate?.(fundName, newPacing[fundName]);
            onFundInfoUpdate?.(fundName, fundInfo);
          }
          
          setLoading(false);
        } else if (statusData.status === 'error') {
          clearInterval(pollInterval);
          throw new Error(statusData.error || 'Task failed');
        }
      }, 3000);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const { maxValue, minValue } = useMemo(() => {
    if (!quarterlyData.length) return { maxValue: 0, minValue: 0 };
    
    const allValues = quarterlyData.flatMap(item => [
      item.marketValueLocal,
      item.contributionsLocal,
      item.distributionsLocal,
      item.modelContributionsLocal,
      item.modelDistributionsLocal,
    ]);
    
    return {
      maxValue: Math.max(...allValues),
      minValue: Math.min(...allValues),
    };
  }, [quarterlyData]);

  const formatDate = (unixTime) => {
    if (!unixTime) return '';
    const date = new Date(unixTime);
    return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
  };

  const formatValue = (val) => {
    if (val == null) return '';
    return `${(val / 1_000_000).toFixed(1)}M`;
  };

  const asOfUnix = portfolioInformation?.asOfDate ? 
    new Date(portfolioInformation.asOfDate).getTime() : null;

  return (
    <Accordion 
      expanded={expanded} 
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        mb: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.shadows[4],
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${fundName}-content`}
        id={`${fundName}-header`}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>{fundName}</Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setShowInputs(!showInputs);
          }}
          sx={{ mr: 1 }}
        >
          <SettingsIcon />
        </IconButton>
      </AccordionSummary>

      <AccordionDetails>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {showInputs && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(fundInfo).map(([key, value]) => {
              if (key === 'fundName') return null;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                  <TextField
                    label={key}
                    value={value ?? ''}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              );
            })}
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={recalculateModel}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Recalculating...' : 'Save & Recalculate'}
              </Button>
            </Grid>
          </Grid>
        )}

        <div style={{ height: 400, width: '100%' }}>
          <ResponsiveContainer>
            <ComposedChart data={quarterlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={formatDate}
              />
              <YAxis
                domain={[
                  Math.floor(minValue / 2_000_000) * 2_000_000,
                  Math.ceil(maxValue / 2_000_000) * 2_000_000,
                ]}
                tickFormatter={formatValue}
              />
              <Tooltip 
                labelFormatter={formatDate} 
                formatter={(value, name) => [formatValue(value), name]}
              />
              <Legend />

              {asOfUnix && (
                <ReferenceLine
                  x={asOfUnix}
                  stroke="#f44336"
                  strokeDasharray="3 3"
                  label={{ 
                    value: 'As-of Date',
                    position: 'top',
                    fill: '#f44336'
                  }}
                />
              )}

              <Area
                type="monotone"
                dataKey="marketValueLocal"
                name="Market Value"
                fill="#3f51b5"
                stroke="#3f51b5"
                fillOpacity={0.3}
              />
              <Bar 
                dataKey="contributionsLocal" 
                name="Contributions" 
                fill="#4caf50"
              />
              <Bar 
                dataKey="distributionsLocal" 
                name="Distributions" 
                fill="#ff9800"
              />
              <Line
                type="monotone"
                dataKey="modelContributionsLocal"
                name="Model Contributions"
                stroke="#e91e63"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="modelDistributionsLocal"
                name="Model Distributions"
                stroke="#009688"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(PacingInvestmentItem);